import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Table, Spinner, Badge } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import { H3 } from '../../../AbstractElements';
import { ArrowLeft, Printer, Download } from 'react-feather';
import { ToastContainer } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';

const ViewLPO = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const lpoId = location.state?.id;
    const viewMode = location.state?.viewMode || 'both';
    const [lpo, setLpo] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const { hasPermission } = useAuth();
    useEffect(() => {
        if (!lpoId) {
            navigate(-1);
            return;
        }

        apiPost("lpos/viewbyid", { id: lpoId })
            .then((res: any) => {
                setLpo(res);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [lpoId]);

    useEffect(() => {
        const originalTitle = document.title;
        if (lpo) {
            document.title = `LPO - ${lpo.lpo_number}`;
        }
        return () => {
            document.title = originalTitle;
        };
    }, [lpo]);

    const handleDownload = () => {
        const doc = new jsPDF() as any;

        // --- 1. HEADER ---
        doc.setFontSize(22);
        doc.setTextColor(40, 40, 40);
        doc.text("Local Purchase Order", 14, 20);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(lpo.lpo_number || '', 14, 26);
        doc.text(`Status: ${lpo.status || ''}`, 14, 31);
        doc.text(`View: ${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}`, 14, 36);

        // Company Info (Right Aligned)
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text("Company Name", 196, 20, { align: "right" });
        doc.setTextColor(100);
        doc.text("Fleet Pro Dept.", 196, 25, { align: "right" });
        doc.text("Dubai, UAE", 196, 30, { align: "right" });

        // --- 2. VENDOR INFO ---
        let currentY = 50;
        doc.setTextColor(0);
        doc.setFontSize(11);
        doc.text("Vendor Details:", 14, 45);

        const renderVendor = (name: string, details: string, label?: string) => {
            if (label) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(label, 14, currentY);
                currentY += 4;
            }
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(name || '', 14, currentY);
            currentY += 5;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(100);
            if (details) {
                const splitAddress = doc.splitTextToSize(details, 80);
                doc.text(splitAddress, 14, currentY);
                currentY += (splitAddress.length * 4) + 2;
            } else {
                currentY += 2;
            }
        };

        if (viewMode === 'labour') {
            renderVendor(lpo.labour_vendor_name || lpo.vendor_name, lpo.labour_vendor_details || '');
        } else if (viewMode === 'parts') {
            renderVendor(lpo.vendor_name, lpo.new_vendor_details);
        } else {
            // Both / Hybrid
            renderVendor(lpo.vendor_name, lpo.new_vendor_details, "Parts Vendor:");

            if (lpo.labour_vendor_name && lpo.labour_vendor_name !== lpo.vendor_name) {
                currentY += 2;
                renderVendor(lpo.labour_vendor_name, lpo.labour_vendor_details, "Labour Vendor:");
            }
        }

        // --- 3. RIGHT SIDE DETAILS ---
        let rightY = 45;
        doc.setTextColor(0);
        doc.setFontSize(10);
        doc.text(`Date: ${new Date(lpo.lpo_date).toLocaleDateString('en-GB')}`, 196, rightY, { align: "right" });
        rightY += 5;
        if (lpo.branch?.branch_name) {
            doc.text(`Branch: ${lpo.branch.branch_name}`, 196, rightY, { align: "right" });
            rightY += 5;
        }
        if (lpo.vehicle) {
            const vehicleInfo = `${lpo.vehicle.registration_number} (${lpo.vehicle.model})`;
            doc.text(`Vehicle: ${vehicleInfo}`, 196, rightY, { align: "right" });
            rightY += 5;
        }
        doc.text(`Category: ${lpo.expense_category}`, 196, rightY, { align: "right" });

        // Calculate Start Y for table, ensuring we clear the vendor section
        let tableStartY = Math.max(currentY + 10, 85);

        // --- 4. TABLES ---

        // Helper to Draw Table
        const drawTable = (title: string, items: any[], isLabour: boolean) => {
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont("helvetica", "bold");
            doc.text(title, 14, tableStartY - 2);

            const tableColumn = ["#", "Description", isLabour ? "Rate" : "Unit Price", "Qty", "Total"];
            const tableRows: any[] = [];
            let sectionTotal = 0;

            items.forEach((item: any, index: number) => {
                const total = Number(item.quantity) * Number(item.unit_price);
                sectionTotal += total;
                tableRows.push([
                    index + 1,
                    item.item_description,
                    Number(item.unit_price).toFixed(2),
                    item.quantity,
                    total.toFixed(2)
                ]);
            });

            autoTable(doc, {
                startY: tableStartY,
                head: [tableColumn],
                body: tableRows,
                headStyles: { fillColor: isLabour ? [240, 173, 78] : [66, 139, 202], textColor: 255 }, // Orange for Labour, Blue for Parts
                theme: 'grid',
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 25, halign: 'right' },
                    3: { cellWidth: 20, halign: 'center' },
                    4: { cellWidth: 30, halign: 'right' }
                },
                foot: [['', '', '', 'Subtotal', sectionTotal.toFixed(2)]],
                footStyles: { fillColor: [245, 245, 245], textColor: 0, fontStyle: 'bold', halign: 'right' }
            });

            tableStartY = doc.lastAutoTable.finalY + 15; // Space for next table
        };


        // A. Parts Table
        if ((viewMode === 'parts' || viewMode === 'both') && lpo.items.some((i: any) => i.item_type === 'Parts')) {
            const partsItems = lpo.items.filter((i: any) => i.item_type === 'Parts');
            drawTable("Spare Parts", partsItems, false);
        }

        // B. Labour Table
        if ((viewMode === 'labour' || viewMode === 'both') && lpo.items.some((i: any) => i.item_type === 'Labour')) {
            const labourItems = lpo.items.filter((i: any) => i.item_type === 'Labour');
            drawTable("Labour Services", labourItems, true);
        }

        // --- 5. GRAND TOTAL ---
        // Calculate relevant total based on viewMode
        let grandTotal = 0;
        if (viewMode === 'parts') grandTotal = lpo.items.filter((i: any) => i.item_type === 'Parts').reduce((acc: number, curr: any) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0);
        else if (viewMode === 'labour') grandTotal = lpo.items.filter((i: any) => i.item_type === 'Labour').reduce((acc: number, curr: any) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0);
        else grandTotal = lpo.items.reduce((acc: number, curr: any) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0);

        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont("helvetica", "bold");
        doc.text(`Grand Total: ${grandTotal.toFixed(2)}`, 196, tableStartY - 5, { align: "right" });


        // --- 6. SIGNATURES ---
        const finalY = tableStartY + 20;

        doc.setDrawColor(0);
        doc.setLineWidth(0.5);

        // Left Signature
        doc.line(14, finalY, 80, finalY);
        doc.text("Authorized Signature", 47, finalY + 5, { align: "center" });

        // Right Signature
        doc.line(130, finalY, 196, finalY);
        doc.text("Vendor Acceptance", 163, finalY + 5, { align: "center" });

        const suffix = viewMode === 'both' ? 'Hybrid' : (viewMode.charAt(0).toUpperCase() + viewMode.slice(1));
        doc.save(`LPO-${lpo.lpo_number}-${suffix}.pdf`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Operations" title="LPO Management" pageTitle={lpo ? `LPO - ${lpo.lpo_number}` : 'LPO Details'} />
            {loading ? <div className="d-flex justify-content-center align-items-center vh-100"><Spinner /></div> : !lpo ? <div className="p-5 text-center">LPO not found</div> : (

                <>
                    <Container fluid>
                        <div className="d-flex justify-content-end mb-3 no-print">
                            <Button color="secondary" className="me-2" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</Button>
                            {hasPermission('lpo.download') && (
                                <Button color="success" className="me-2" onClick={handleDownload}><Download size={16} /> Download</Button>)}
                            {hasPermission('lpo.print') && (
                                <Button color="primary" onClick={handlePrint}><Printer size={16} /> Print</Button>
                            )}
                        </div>

                        <Card className="print-section">
                            <CardBody className="p-5">
                                {/* Header */}
                                <div className="d-flex justify-content-between mb-5">
                                    <div>
                                        <h2 className="mb-0 text-primary">Local Purchase Order</h2>
                                        <h5 className="text-muted">{lpo.lpo_number}</h5>
                                        <Badge color={lpo.status === 'Approved' ? 'success' : 'warning'}>{lpo.status}</Badge>
                                    </div>
                                    <div className="text-end">
                                        <h4>Company Name</h4>
                                        <p>Fleet Pro Dept.<br />Dubai, UAE</p>
                                    </div>
                                </div>

                                {/* Info Section */}
                                <Row className="mb-5">
                                    <Col md={6}>
                                        <h6 className="text-uppercase text-muted">Vendor</h6>
                                        {viewMode === 'labour' ? (
                                            <>
                                                <h4>{lpo.labour_vendor_name || lpo.vendor_name}</h4>
                                                {lpo.labour_vendor_details && <p className="text-muted">{lpo.labour_vendor_details}</p>}
                                            </>
                                        ) : (
                                            <>
                                                <h4>{lpo.vendor_name}</h4>
                                                {lpo.new_vendor_details && <p className="text-muted">{lpo.new_vendor_details}</p>}
                                                {viewMode === 'both' && lpo.labour_vendor_name && lpo.labour_vendor_name !== lpo.vendor_name && (
                                                    <div className="mt-3">
                                                        <h6 className="text-uppercase text-muted">Labour Vendor</h6>
                                                        <h4>{lpo.labour_vendor_name}</h4>
                                                        {lpo.labour_vendor_details && <p className="text-muted">{lpo.labour_vendor_details}</p>}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Col>
                                    <Col md={6} className="text-end">
                                        <h6 className="text-uppercase text-muted">Details</h6>
                                        <p className="mb-1"><strong>Date:</strong> {lpo.lpo_date ? new Date(lpo.lpo_date).toLocaleDateString('en-GB') : ''}</p>
                                        <p className="mb-1"><strong>Branch:</strong> {lpo.branch?.branch_name}</p>
                                        {lpo.vehicle && <p className="mb-1"><strong>Vehicle:</strong> {lpo.vehicle.registration_number} ({lpo.vehicle.model})</p>}
                                        {lpo.odometer_reading && <p className="mb-1"><strong>Odometer:</strong> {lpo.odometer_reading} km</p>}
                                        <p className="mb-1"><strong>Category:</strong> {lpo.expense_category}</p>
                                    </Col>
                                </Row>

                                {/* Parts Items Table */}
                                {lpo.items.some((i: any) => i.item_type === 'Parts') && (viewMode === 'parts' || viewMode === 'both') && (
                                    <>
                                        <H3 className="text-primary mt-4 mb-2">Spare Parts</H3>
                                        <Table bordered className="mb-4">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Description</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">Unit Price</th>
                                                    <th className="text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lpo.items.filter((i: any) => i.item_type === 'Parts').map((item: any, index: number) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.item_description}</td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{Number(item.unit_price).toFixed(2)}</td>
                                                        <td className="text-end">{Number(item.total_price).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}

                                {/* Labour Items Table */}
                                {lpo.items.some((i: any) => i.item_type === 'Labour') && (viewMode === 'labour' || viewMode === 'both') && (
                                    <>
                                        <H3 className="text-warning mt-4 mb-2">Labour</H3>
                                        <Table bordered className="mb-4">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Description</th>
                                                    <th className="text-end">Rate</th>
                                                    <th className="text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lpo.items.filter((i: any) => i.item_type === 'Labour').map((item: any, index: number) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.item_description}</td>
                                                        <td className="text-end">{Number(item.unit_price).toFixed(2)}</td>
                                                        <td className="text-end">{Number(item.total_price).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}

                                {/* Grand Total */}
                                <div className="text-end mt-4 border-top pt-3">
                                    <h4>Grand Total: <span className="text-primary">{Number(lpo.amount).toFixed(2)} </span></h4>
                                </div>

                                {/* Signatures */}
                                <Row className="mt-5 pt-5 mb-4">
                                    <Col xs={6}>
                                        <div className="border-top pt-2 w-75 text-center">
                                            <p>Authorized Signature</p>
                                        </div>
                                    </Col>
                                    <Col xs={6} className="d-flex justify-content-end">
                                        <div className="border-top pt-2 w-75 text-center">
                                            <p>Vendor Acceptance</p>
                                        </div>
                                    </Col>
                                </Row>

                            </CardBody>
                        </Card>
                    </Container>

                    <style>
                        {`
                    @media print {
                        @page { margin: 0; }
                        .page-body { margin: 0; padding: 0; }
                        .sidebar-wrapper, .page-header, .footer, .no-print, .tap-top, Breadcrumbs, .customizer-links, .customizer-contain, .sidebar-pannle-main, .setting-sidebar { display: none !important; }
                        .card { border: none !important; box-shadow: none !important; }
                    }
                `}
                    </style>
                </>
            )}
            <ToastContainer />
        </div>
    );
};

export default ViewLPO;
